import { useMutation, useQuery, useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import request from 'app/api/request';
import { i18n } from 'app/utils/i18n';
import { MissingSheet, parseSheetOptions, parseXlsxData } from './utils';
import { queryKeys as documentQueryKeys } from 'app/api/documents/document-queries';
import { tagQueryKeys } from 'app/api/tag-queries/tags';
import { invalidateOnValueSave } from 'app/api/tag-queries/tagCacheInvalidation';
import ServerErrorHandler from 'app/ErrorHandler';
import { showMessage } from 'app/utils/messages';
import { EventType, dispatchMessage } from 'app/useToasts';

export const queryKeys = {
  referenceData: 'REFERENCE_DATA',
  referenceDataList: 'REFERENCE_DATA_LIST',
  parsedSheet: 'PARSED_SHEET',
  xlsxFile: 'XLSX_FILE',
  xlsxFiles: 'XLSX_FILES',
};

export function useXlsxFile(fileRevisionId) {
  return useQuery(
    [queryKeys.xlsxFile, fileRevisionId],
    async () => {
      const { data } = await request.get(`/gaby/files/${fileRevisionId}`);
      return {
        selectedXlsx: data,
        sheetOptions: parseSheetOptions(data),
      };
    },
    {
      enabled: !!fileRevisionId,
      onError: ServerErrorHandler,
    }
  );
}

export function useXlsxFiles(documentId) {
  return useQuery(
    [queryKeys.xlsxFiles, documentId],
    async () => {
      const res = await request.get(`/gaby/files?documentSuperId=${documentId}&type=xlsx`);
      return res.data;
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useReferenceData(referenceDataId, documentId, revisionId) {
  // TODO this is sometimes called in situations when we actually need to fetch
  // a specifi version of a reference data. This method always fetches the latest one.
  return useQuery(
    [queryKeys.referenceData, documentId, referenceDataId],
    async () => {
      const res = await request.get(
        `/gaby/reference-data?documentSuperId=${documentId}&superId=${referenceDataId}&revisionId=${revisionId}&current=true&metadata=true`
      );

      return res.data[0];
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useReferenceDataByRevisionId(revisionId) {
  return useQuery(
    [queryKeys.referenceData, { revisionId }],
    async () => {
      const res = await request.get(`/gaby/reference-data/${revisionId}`);
      return res.data;
    },
    {
      onError: ServerErrorHandler,
      enabled: !!revisionId,
    }
  );
}

export function useUpdateReferenceData() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ document, referenceData }) => {
      const res = await request.put(`/gaby/reference-data`, referenceData);
      return res;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_, { document, referenceData }) => {
        queryClient.invalidateQueries(queryKeys.referenceData, {
          referenceDataId: referenceData.superId,
          documentId: document.superId,
        });
        queryClient.invalidateQueries(documentQueryKeys.document, document.superId);
        queryClient.invalidateQueries(queryKeys.referenceDataList);
        invalidateOnValueSave(queryClient, document.superId);
      },
    }
  );
}

export function useUpdateReferenceTableCssClass() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentSuperId, referenceDataSuperId, cssClass }) => {
      const res = await request.patch(`/gaby/reference-data/${documentSuperId}/${referenceDataSuperId}/css-class`, { cssClass });
      return res;
    },
    {
      onMutate: async ({ documentSuperId, referenceDataSuperId, cssClass }) => {
        await queryClient.cancelQueries(documentQueryKeys.document, documentSuperId);
        queryClient.setQueriesData([documentQueryKeys.document, documentSuperId], (old) => {
          if (!old) return old;
          const updated = JSON.parse(JSON.stringify(old));
          const refEntry = updated.content?.referenceData?.find((r) => r.superId === referenceDataSuperId);
          if (refEntry) {
            refEntry.cssClass = cssClass || undefined;
          }
          return updated;
        });
      },
      onError: ServerErrorHandler,
      onSettled: (_, __, { documentSuperId }) => {
        queryClient.invalidateQueries(documentQueryKeys.document, documentSuperId);
      },
    }
  );
}

export function useCreateReferenceData() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ document, referenceData }) => {
      const res = await request.post(`/gaby/reference-data`, referenceData);
      return res;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_, { document }) => {
        queryClient.invalidateQueries(queryKeys.referenceDataList);
        queryClient.invalidateQueries(documentQueryKeys.document, document.superId);
      },
    }
  );
}

export function useDeleteReferenceData() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ documentId, referenceData }) => {
      await request.del(`/gaby/reference-data/${documentId}/${referenceData.revisionId}`);
    },
    {
      onSuccess: (_, { documentId, referenceData }) => {
        queryClient.invalidateQueries(queryKeys.referenceDataList);
        queryClient.invalidateQueries(queryKeys.referenceData, {
          documentId,
          referenceDataId: referenceData.superId,
        });
        queryClient.invalidateQueries(documentQueryKeys.document, documentId);
      },
      onError: (error) => {
        const response = JSON.parse(error.request.response);
        if (response.message !== 'reference-in-use') {
          ServerErrorHandler(error);
        }
      },
    }
  );
}

export function useReferenceList(document) {
  const CHUNK_THRESHOLD = 100;
  const CHUNK_SIZE = 50;
  return useQuery(
    [queryKeys.referenceDataList, document?.revisionId],
    async () => {
      if (!Array.isArray(document.content.referenceData)) {
        return Promise.resolve([]);
      }
      const revisionIds = document.content.referenceData.map((ref) => ref.revisionId);
      if (revisionIds.length === 0) return [];
      if (revisionIds.length <= CHUNK_THRESHOLD) {
        const res = await request.post('/gaby/reference-data/batch', {
          revisionIds,
          documentSuperId: document.superId,
        });
        return res.data;
      } else {
        const chunks = [];
        for (let i = 0; i < revisionIds.length; i += CHUNK_SIZE) {
          chunks.push(revisionIds.slice(i, i + CHUNK_SIZE));
        }

        let allReferenceData = [];

        for (let i = 0; i < chunks.length; i++) {
          const chunkIds = chunks[i];
          const res = await request.post('/gaby/reference-data/batch', {
            revisionIds: chunkIds,
            documentSuperId: document.superId,
          });
          allReferenceData = [...allReferenceData, ...res.data];
        }

        return allReferenceData;
      }
    },
    {
      enabled: !!document,
      onError: ServerErrorHandler,
    }
  );
}

export function useReferenceTranslations(documentSuperId) {
  return useQuery(
    [queryKeys.referenceData, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/reference-data/xliff/metadata/${documentSuperId}`);
      return res.data;
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export function useUploadXliff(documentSuperId) {
  const queryClient = useQueryClient();
  return useMutation(
    [queryKeys.referenceData, documentSuperId],
    async (xliffId) => {
      await request.post(`/gaby/reference-data/xliff/${documentSuperId}?xliffId=${xliffId}`);
    },
    {
      enabled: !!documentSuperId,
      onError: () => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'error',
          text: i18n('reference-data.refresh-error'),
          removeTime: 3000,
        });
      },
      onSuccess: () => {
        dispatchMessage(EventType.ADD_MESSAGE, {
          type: 'success',
          text: i18n('reference-data.refresh-success'),
          removeTime: 3000,
        });
        queryClient.invalidateQueries([queryKeys.referenceDataList, documentSuperId, undefined]);
      },
    }
  );
}

export function useParsedSheet(data, selectedSheet, locale) {
  return useQuery(
    [queryKeys.parsedSheet, data?.revisionId, { selectedSheet, locale }],
    () => {
      // TODO use service worker or pass over to server
      return new Promise((resolve, reject) => {
        setImmediate(() => {
          try {
            resolve(parseXlsxData(data, selectedSheet, locale));
          } catch (e) {
            reject(e);
          }
        });
      });
    },
    {
      retry: false,
      enabled: !!data && !!selectedSheet && !!locale,
      onError: (error) => {
        if (!error instanceof MissingSheet) showMessage('error', i18n('reference-data.failed-parsing-xlsx'));
      },
    }
  );
}

export function useSelectedData(sheetData, selection, currentReferenceData) {
  const status = {
    isLoading: sheetData.isLoading,
    isSuccess: sheetData.isSuccess,
    isError: sheetData.isError,
    isIdle: sheetData.isIdle,
  };

  if (!sheetData.isSuccess) {
    return status;
  }

  if (!selection) {
    return {
      isLoading: false,
      isSuccess: false,
      isError: true,
      isIdle: false,
    };
  }

  const startRow = Math.min(selection.start.i, selection.end.i);
  const endRow = Math.max(selection.start.i, selection.end.i) + 1; // +1 is needed as .slice([end]) is excluding
  const startColumn = Math.min(selection.start.j, selection.end.j);
  const endColumn = Math.max(selection.start.j, selection.end.j) + 1; // +1 is needed as .slice([end]) is excluding

  const selectedData = sheetData.data.slice(startRow, endRow).map((row) => row.slice(startColumn, endColumn));

  const old = currentReferenceData.selectionContent;
  selectedData.forEach((row, y) => {
    row.forEach((cell, x) => {
      const oldCell = old?.[y]?.[x];
      cell.format = oldCell?.format;
      cell.styling = oldCell?.styling;
      cell.background = oldCell?.background;
      cell.border = oldCell?.border;
      if (oldCell?.value === cell?.value) {
        cell.locales = oldCell?.locales;
      }
    });
  });

  return {
    ...status,
    data: selectedData,
  };
}

export function useGetXliffDownload(documentId) {
  return useMutation(
    async () => {
      const res = await request.get(`/gaby/reference-data/xliff/${documentId}`);
      return { data: res.data, targetLocale: res.data.targetLocale, sourceLocale: res.data.sourceLocale };
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useAddTagsToCells() {
  const queryClient = useQueryClient();
  return useMutation(
    async ({ referenceData, tagsToAdd }) => {
      // tagsToAdd: [{ row, col, tagId }, ...]
      const updatedContent = referenceData.selectionContent.map((row) => row.map((cell) => ({ ...cell })));

      tagsToAdd.forEach(({ row, col, tagId }) => {
        if (updatedContent[row]?.[col]) {
          const cell = updatedContent[row][col];
          cell.tags = cell.tags ? [...cell.tags, tagId] : [tagId];
        }
      });

      // Generate a new revisionId since reference data is versioned
      const res = await request.put('/gaby/reference-data', {
        ...referenceData,
        revisionId: uuidv4(),
        selectionContent: updatedContent,
      });
      return res;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_, { referenceData, document }) => {
        queryClient.invalidateQueries(queryKeys.referenceData);
        queryClient.invalidateQueries(queryKeys.referenceDataList);
        // Tag metadata (textSnippet) is derived from cell.tags server-side —
        // the refetch fired by tag creation raced this write, so refetch again.
        queryClient.invalidateQueries(tagQueryKeys.tagsByDocument);
        queryClient.invalidateQueries(tagQueryKeys.tagsByDocumentPaginated);
        if (document) {
          queryClient.invalidateQueries(documentQueryKeys.document, document.superId);
        }
      },
    }
  );
}
